import React from 'react';
import {
  Chip,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { DATE_RANGE_OPTIONS, GROUP_BY_OPTIONS } from '../../constants';
import { DateTypeToggle } from 'modules/finance/ui/components/DateTypeToggle';
import { CustomDateRangeInputs } from 'modules/finance/ui/components/CustomDateRangeInputs';
import { FinanceInsurancePerformanceReportGroupBy } from '@trnsact/trnsact-shared-types';
import { FinanceDateRange, FinanceDealStatus, FinancePaymentType, PerformanceFiltersState } from '../../types';

interface Props {
  filters: PerformanceFiltersState;
  onChange: (filters: PerformanceFiltersState) => void;
}

const FILTER_LABEL_SX = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: 0.4,
  color: 'text.secondary',
  whiteSpace: 'nowrap',
} as const;
const TOGGLE_BTN_SX = {
  px: 1.5,
  py: 0.5,
  fontSize: 11,
  fontWeight: 500,
  textTransform: 'none',
  border: 'none !important',
  borderRadius: '6px !important',
  '&.Mui-selected': { bgcolor: 'background.paper', color: 'text.primary', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
} as const;
const TOGGLE_GROUP_SX = {
  bgcolor: 'action.hover',
  borderRadius: '8px',
  p: '3px',
  gap: 0,
  '& .MuiToggleButtonGroup-grouped': { m: 0 },
} as const;

export const PerformanceFilters = ({ filters, onChange }: Props) => {
  const set = <K extends keyof PerformanceFiltersState>(key: K, value: PerformanceFiltersState[K]) =>
    onChange({ ...filters, [key]: value });

  return (
    <Stack spacing={1} mb={1.5}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <Typography sx={FILTER_LABEL_SX}>Compare by</Typography>
        <Select
          size="small"
          value={filters.groupBy}
          onChange={(e: SelectChangeEvent) =>
            set('groupBy', e.target.value as FinanceInsurancePerformanceReportGroupBy)
          }
          sx={{ fontSize: 13, '& .MuiSelect-select': { py: '6px', px: '10px' } }}
        >
          {GROUP_BY_OPTIONS.map(opt => (
            <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 13 }}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>

        <DateTypeToggle value={filters.dateType} onChange={v => set('dateType', v)} />

        <Typography sx={{ ...FILTER_LABEL_SX, ml: 0.75 }}>Date</Typography>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
          {DATE_RANGE_OPTIONS.map(opt => (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => set('dateRange', opt.value as FinanceDateRange)}
              variant={filters.dateRange === opt.value ? 'filled' : 'outlined'}
              color={filters.dateRange === opt.value ? 'primary' : 'default'}
              sx={{ fontSize: 11, fontWeight: 500, height: 26, cursor: 'pointer' }}
            />
          ))}
          {filters.dateRange === 'custom' && (
            <CustomDateRangeInputs value={filters.customDateRange} onChange={v => set('customDateRange', v)} />
          )}
        </Stack>
      </Stack>

      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" width="100%">
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography sx={FILTER_LABEL_SX}>Deal status</Typography>
          <ToggleButtonGroup
            exclusive
            value={filters.dealStatus}
            onChange={(_, v: FinanceDealStatus | null) => {
              if (v) set('dealStatus', v);
            }}
            sx={TOGGLE_GROUP_SX}
          >
            <ToggleButton value="ALL" sx={TOGGLE_BTN_SX}>
              All
            </ToggleButton>
            <ToggleButton value="PIPELINE" sx={TOGGLE_BTN_SX}>
              Pipeline
            </ToggleButton>
            <ToggleButton value="CLOSED_WON" sx={TOGGLE_BTN_SX}>
              Closed won
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography sx={FILTER_LABEL_SX}>Payment type</Typography>
          <ToggleButtonGroup
            exclusive
            value={filters.paymentType}
            onChange={(_, v: FinancePaymentType | null) => {
              if (v) set('paymentType', v);
            }}
            sx={TOGGLE_GROUP_SX}
          >
            <ToggleButton value="ALL" sx={TOGGLE_BTN_SX}>
              All
            </ToggleButton>
            <ToggleButton value="FINANCING" sx={TOGGLE_BTN_SX}>
              Financing
            </ToggleButton>
            <ToggleButton value="CASH" sx={TOGGLE_BTN_SX}>
              Cash
            </ToggleButton>
            <ToggleButton value="CREDIT_LINE" sx={TOGGLE_BTN_SX}>
              Credit Line
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Stack>
    </Stack>
  );
};
